import type { RefCallback } from 'react';

import { useCallback, useMemo, useState } from 'react';

export const useIntersectionObserver = <T extends HTMLElement = HTMLElement>(
  onIntersect: (() => void) | null,
  options: IntersectionObserverInit = { threshold: 1, root: null, rootMargin: '0px' },
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);

  const { root, rootMargin, threshold } = options;

  const ref = useCallback<RefCallback<T>>(
    (element) => {
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setIsIntersecting(true);
            onIntersect?.();
          } else {
            setIsIntersecting(false);
          }
        },
        { root, rootMargin, threshold },
      );

      observer.observe(element);

      return () => observer.disconnect();
    },
    [onIntersect, root, rootMargin, threshold],
  );

  return useMemo(() => ({ isIntersecting, ref }), [isIntersecting, ref]);
};
